import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { FaRupeeSign, FaHistory, FaExclamationTriangle, FaCreditCard, FaCalendarAlt, FaReceipt, FaCheckCircle, FaBus, FaClipboardList } from 'react-icons/fa';
import '../../styles/StudentPayments.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://krmmadhubanibackend.onrender.com';

const StudentPayments = () => {
  const { user, isStudent } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [student, setStudent] = useState(null);
  const [currentDues, setCurrentDues] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [totalPaid, setTotalPaid] = useState(0);
  const [feeBreakdown, setFeeBreakdown] = useState(null);

  useEffect(() => {
    if (!isStudent || !user?.Rno) {
      navigate('/login', { replace: true });
      return;
    }
    fetchPaymentData();
  }, [isStudent, user]);

  const fetchPaymentData = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await fetch(`${API_BASE_URL}/api/auth/student-payments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rollNo: user.Rno }),
      });
      const data = await res.json();

      if (!data.success) {
        setError(data.message || 'Unable to fetch payment data.');
        return;
      }

      setStudent(data.student);
      setCurrentDues(data.currentDues || 0);
      setFeeBreakdown(data.feeBreakdown || null);
      setTransactions(data.transactions || []);
      setTotalPaid(
        (data.transactions || []).reduce((sum, t) => sum + (parseFloat(t.amount_paid) || 0), 0)
      );
    } catch (err) {
      console.error('Fetch payment data error:', err);
      setError('Something went wrong. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Intl.DateTimeFormat('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(new Date(dateString));
  };

  const formatDateTime = (dateString) => {
    return new Intl.DateTimeFormat('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateString));
  };

  // Dues: negative FeeDues = student owes, positive = advance
  const owes = currentDues < 0 ? Math.abs(currentDues) : 0;
  const advance = currentDues > 0 ? currentDues : 0;

  if (loading) {
    return (
      <div className="sp-container">
        <div className="sp-loading">
          <div className="sp-spinner"></div>
          <p>Loading your payment details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sp-container">
        <div className="sp-error-state">
          <FaExclamationTriangle />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="sp-container">
      {/* Header */}
      <div className="sp-header">
        <div className="sp-header-info">
          <h1><FaRupeeSign /> My Payments</h1>
          <p className="sp-student-info">
            {student?.Name} &middot; Roll No: {student?.Rno} &middot; Class: {student?.Class}
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="sp-cards">
        <div className={`sp-card ${owes > 0 ? 'sp-card-danger' : 'sp-card-success'}`}>
          <div className="sp-card-icon">
            {owes > 0 ? <FaExclamationTriangle /> : <FaRupeeSign />}
          </div>
          <div className="sp-card-content">
            <span className="sp-card-label">{owes > 0 ? 'Outstanding Dues' : 'Advance Balance'}</span>
            <span className="sp-card-value">₹{owes > 0 ? owes : advance}</span>
          </div>
        </div>

        <div className="sp-card sp-card-info">
          <div className="sp-card-icon"><FaHistory /></div>
          <div className="sp-card-content">
            <span className="sp-card-label">Total Transactions</span>
            <span className="sp-card-value">{transactions.length}</span>
          </div>
        </div>
      </div>

      {/* Current Month Fee Breakdown */}
      {feeBreakdown && (
        <div className="sp-breakdown">
          <h2><FaClipboardList /> Current Month Fee Breakdown</h2>
          <div className="sp-breakdown-grid">
            {/* Previous Dues */}
            {feeBreakdown.previousDues < 0 && (
              <div className="sp-bd-item sp-bd-danger">
                <span className="sp-bd-label">Previous Dues</span>
                <span className="sp-bd-value">₹{Math.abs(feeBreakdown.previousDues)}</span>
              </div>
            )}
            {feeBreakdown.previousDues > 0 && (
              <div className="sp-bd-item sp-bd-success">
                <span className="sp-bd-label">Advance Balance</span>
                <span className="sp-bd-value">−₹{feeBreakdown.previousDues}</span>
              </div>
            )}

            {/* Monthly Fee */}
            <div className={`sp-bd-item ${feeBreakdown.paidMonthly ? 'sp-bd-paid' : ''}`}>
              <span className="sp-bd-label">
                Monthly Fee
                {feeBreakdown.paidMonthly && <span className="sp-bd-badge"><FaCheckCircle /> Paid</span>}
              </span>
              <span className="sp-bd-value">{feeBreakdown.paidMonthly ? <s>₹{feeBreakdown.monthlyFee}</s> : `₹${feeBreakdown.monthlyFee}`}</span>
            </div>

            {/* Exam Fee */}
            {feeBreakdown.isExamMonth && (
              <div className={`sp-bd-item ${feeBreakdown.paidExam ? 'sp-bd-paid' : ''}`}>
                <span className="sp-bd-label">
                  Examination Fee
                  {feeBreakdown.paidExam && <span className="sp-bd-badge"><FaCheckCircle /> Paid</span>}
                </span>
                <span className="sp-bd-value">{feeBreakdown.paidExam ? <s>₹{feeBreakdown.examFee}</s> : `₹${feeBreakdown.examFee}`}</span>
              </div>
            )}
            {!feeBreakdown.isExamMonth && (
              <div className="sp-bd-item sp-bd-na">
                <span className="sp-bd-label">Examination Fee</span>
                <span className="sp-bd-value">N/A</span>
              </div>
            )}

            {/* Transport Fee */}
            {feeBreakdown.hasTransport ? (
              <div className={`sp-bd-item ${feeBreakdown.paidTransport ? 'sp-bd-paid' : ''}`}>
                <span className="sp-bd-label">
                  <FaBus /> Transport Fee
                  {feeBreakdown.paidTransport && <span className="sp-bd-badge"><FaCheckCircle /> Paid</span>}
                </span>
                <span className="sp-bd-value">{feeBreakdown.paidTransport ? <s>₹{feeBreakdown.transportFee}</s> : `₹${feeBreakdown.transportFee}`}</span>
              </div>
            ) : (
              <div className="sp-bd-item sp-bd-na">
                <span className="sp-bd-label"><FaBus /> Transport Fee</span>
                <span className="sp-bd-value">Not Enrolled</span>
              </div>
            )}

            {/* Total Due */}
            <div className={`sp-bd-item sp-bd-total ${feeBreakdown.totalDue > 0 ? 'sp-bd-total-due' : 'sp-bd-total-clear'}`}>
              <span className="sp-bd-label">Total Amount Due</span>
              <span className="sp-bd-value">₹{feeBreakdown.totalDue}</span>
            </div>
          </div>
        </div>
      )}

      {/* Pay Online Button */}
      {feeBreakdown && feeBreakdown.totalDue > 0 && (
        <div className="sp-pay-section">
          <button className="sp-pay-btn" onClick={() => alert('Online payment gateway coming soon!')}>
            <FaCreditCard /> Pay ₹{feeBreakdown.totalDue} Online
          </button>
          <p className="sp-pay-hint">Secure online payment via UPI / Card / Net Banking</p>
        </div>
      )}

      {/* Transaction History */}
      <div className="sp-history">
        <h2><FaReceipt /> Transaction History</h2>

        {transactions.length === 0 ? (
          <div className="sp-empty">
            <p>No payment transactions found.</p>
          </div>
        ) : (
          <div className="sp-table-wrap">
            <table className="sp-table">
              <thead>
                <tr>
                  <th><FaCalendarAlt /> Date</th>
                  <th>Previous Dues</th>
                  <th>Monthly Fee</th>
                  <th>Transport</th>
                  <th>Exam Fee</th>
                  <th>Total Due</th>
                  <th>Amount Paid</th>
                  <th>New Balance</th>
                  <th>Method</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((txn) => {
                  return (
                    <tr key={txn.id}>
                      <td className="sp-date-cell">{formatDateTime(txn.created_at)}</td>
                      <td className={txn.fee_dues < 0 ? 'sp-neg' : txn.fee_dues > 0 ? 'sp-pos' : ''}>
                        {txn.fee_dues < 0 ? '-' : ''}₹{Math.abs(txn.fee_dues || 0)}
                      </td>
                      <td>₹{txn.current_month_fee || 0}</td>
                      <td>₹{txn.transport_fee || 0}</td>
                      <td>₹{txn.examination_fee || 0}</td>
                      <td className="sp-total-due">₹{txn.total_amount || 0}</td>
                      <td className="sp-amount-paid">₹{txn.amount_paid || 0}</td>
                      <td className={txn.new_dues < 0 ? 'sp-neg' : txn.new_dues > 0 ? 'sp-pos' : ''}>
                        {txn.new_dues < 0 && '−'}
                        {txn.new_dues > 0 && '+'}
                        ₹{Math.abs(txn.new_dues || 0)}
                      </td>
                      <td>
                        <span className={`sp-method ${(txn.payment_method || '').toLowerCase()}`}>
                          {txn.payment_method || 'N/A'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentPayments;
